import { useState, useEffect, useCallback } from 'react';
import { searchOrganizations, getUserOrganizations, Organization } from '../utils/api';

const RECENT_ORGANIZATIONS_KEY = 'recentOrganizations';
const MAX_RECENT_ORGANIZATIONS = 5;

export const useOrganizationSearch = (currentOrganizationId?: string) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [recentOrganizations, setRecentOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Carregar organizações recentes do localStorage
  useEffect(() => {
    const loadRecentOrganizations = () => {
      try {
        const stored = localStorage.getItem(RECENT_ORGANIZATIONS_KEY);
        if (stored) {
          const recentIds = JSON.parse(stored) as string[];
          // Buscar dados completos das organizações recentes
          getUserOrganizations().then(allOrgs => {
            const recent = allOrgs.filter(org => recentIds.includes(org.id));
            // Ordenar: atual primeiro, depois as outras recentes
            const sortedRecent = recent.sort((a, b) => {
              if (currentOrganizationId) {
                if (a.id === currentOrganizationId) return -1;
                if (b.id === currentOrganizationId) return 1;
              }
              return 0;
            });
            setRecentOrganizations(sortedRecent);
          });
        }
      } catch (error) {
        console.error('Error loading recent organizations:', error);
      }
    };

    loadRecentOrganizations();
  }, [currentOrganizationId]);

  // Debounce para busca
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (searchTerm.trim()) {
        setIsSearching(true);
        setIsLoading(true);
        try {
          const results = await searchOrganizations(searchTerm);
          setOrganizations(results);
        } catch (error) {
          console.error('Error searching organizations:', error);
          setOrganizations([]);
        } finally {
          setIsLoading(false);
          setIsSearching(false);
        }
      } else {
        setOrganizations([]);
        setIsSearching(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Função para adicionar organização aos recentes
  const addToRecent = useCallback((organization: Organization) => {
    setRecentOrganizations(prev => {
      // Remover se já existe
      const filtered = prev.filter(org => org.id !== organization.id);
      // Adicionar no início
      const updated = [organization, ...filtered].slice(0, MAX_RECENT_ORGANIZATIONS);
      
      // Ordenar: atual primeiro, depois as outras recentes
      const sortedUpdated = updated.sort((a, b) => {
        if (currentOrganizationId) {
          if (a.id === currentOrganizationId) return -1;
          if (b.id === currentOrganizationId) return 1;
        }
        return 0;
      });
      
      try {
        localStorage.setItem(RECENT_ORGANIZATIONS_KEY, JSON.stringify(sortedUpdated.map(org => org.id)));
      } catch (error) {
        console.error('Error saving recent organizations:', error);
      }
      
      return sortedUpdated;
    });
  }, [currentOrganizationId]);

  // Função para limpar busca
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setOrganizations([]);
    setIsSearching(false);
  }, []);

  // Determinar quais organizações mostrar
  const displayOrganizations = searchTerm.trim() ? organizations : recentOrganizations;

  return {
    searchTerm,
    setSearchTerm,
    organizations: displayOrganizations,
    recentOrganizations,
    isLoading,
    isSearching,
    addToRecent,
    clearSearch,
    hasSearchTerm: searchTerm.trim().length > 0
  };
};
